import { isResource } from '@medplum/core'
import type {
  Address,
  CodeableConcept,
  Coding,
  ContactPoint,
  Identifier,
  Patient,
} from '@medplum/fhirtypes'
import { compact, orderBy } from 'lodash-es'
import {
  codeableConcept,
  codeFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  LEAD_FLAG_SYSTEM,
  ORGANIZATION_SYSTEM,
  SEEN_HEALTH_ORG_IDENTIFIER,
} from '#src/codeSystems.ts'
import { Extension, Tag } from '#src/decorators.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import {
  LEAD_FLAGS,
  type LeadFlagDefinition,
} from '#src/models/intake/leadFlags.ts'
import { LEAD_TAG } from '#src/models/patients/index.ts'
import { Participant } from '#src/models/patients/participant.ts'
import type { ProfileLanguageCode } from '#src/models/profile.ts'
import type { QueryTypes } from '#src/search.ts'
import { getTags, hasTag } from '#src/tags.ts'
import { isOfType, recordValues } from '#src/utils.ts'

export const RESPONSIBLE_PARTY_SELF = 'Self' as const

export const LeadStatus = {
  HOT: { code: 'hot' as const, display: '1 - Hot' },
  WARM: { code: 'warm' as const, display: '2 - Warm' },
  LUKE_WARM: { code: 'luke-warm' as const, display: '3 - Luke Warm' },
  COLD: { code: 'cold' as const, display: '4 - Cold' },
} satisfies Record<string, Coding>

export type LeadStatus = (typeof LeadStatus)[keyof typeof LeadStatus]['code']

export type ResponsibleParty = 'Self' | 'Spouse' | 'Child' | 'Other'

export interface LeadContactMethod {
  contactMethodType: CodeableConcept
  contactMethodValue: string
  contactMethodPreferred: boolean
}

export class Lead extends Participant {
  static readonly RESOURCE_TYPE = 'Patient'

  static isInstance(value: unknown): value is Patient {
    return (
      isResource(value) && isOfType(value, 'Patient') && hasTag(value, LEAD_TAG)
    )
  }

  constructor(data: Omit<Patient, 'active' | 'resourceType'>) {
    super({
      active: false,
      // Every participant belongs to the Seen Health PACE tenant. Set via an
      // identifier-based conditional reference so no org lookup is needed at
      // construction; Medplum resolves it server-side on write.
      managingOrganization: {
        reference: `Organization?identifier=${ORGANIZATION_SYSTEM}|${SEEN_HEALTH_ORG_IDENTIFIER}`,
      },
      ...data,
    })
    this.isLead = true
  }

  get flags(): LeadFlagDefinition[] {
    const flags = getTags(this.resource, LEAD_FLAG_SYSTEM)
      .map((tag) => LEAD_FLAGS[tag.code as keyof typeof LEAD_FLAGS])
      .filter((flag): flag is LeadFlagDefinition => flag !== undefined)
    return orderBy(flags, ['priority'], ['asc'])
  }

  @Tag({
    system: 'https://www.seenhealth.org/fhir/CodeSystem/medicaid-eligible',
  })
  _medicaidEligible?: Coding

  get medicaidEligibleBoolean(): boolean | undefined {
    return this._medicaidEligible?.code !== 'false'
  }

  set medicaidEligibleBoolean(value: boolean | undefined) {
    if (value === undefined) {
      this._medicaidEligible = undefined
    } else {
      this.setTag(
        'https://www.seenhealth.org/fhir/CodeSystem/medicaid-eligible',
        value ? 'true' : 'false'
      )
    }
  }

  @Tag({
    system: 'https://www.seenhealth.org/fhir/CodeSystem/medicare-eligible',
  })
  _medicareEligible?: Coding

  get medicareEligibleBoolean(): boolean | undefined {
    const tag = this.getTag(
      'https://www.seenhealth.org/fhir/CodeSystem/medicare-eligible'
    )
    return tag?.code !== 'false'
  }

  set medicareEligibleBoolean(value: boolean | undefined) {
    if (value === undefined) {
      this._medicareEligible = undefined
    } else {
      this.setTag(
        'https://www.seenhealth.org/fhir/CodeSystem/medicare-eligible',
        value ? 'true' : 'false'
      )
    }
  }

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/other-notes',
    kind: 'valueString',
  })
  otherNotes?: string

  @Tag({
    system: 'https://www.seenhealth.org/fhir/CodeSystem/lead-status',
  })
  _leadStatus?: Coding

  get leadStatus(): LeadStatus | undefined {
    return this._leadStatus?.code as LeadStatus | undefined
  }

  set leadStatus(value: LeadStatus | undefined) {
    this._leadStatus = recordValues(LeadStatus).find(
      (status) => status.code === value
    )
  }

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/medicare-medical-status',
    kind: 'valueString',
  })
  medicareMedicalStatus?: string

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/responsible-party',
    kind: 'valueCodeableConcept',
  })
  private _responsibleParty?: CodeableConcept

  get responsibleParty(): ResponsibleParty | undefined {
    return codeFromCodeableConcept(this._responsibleParty) as
      | ResponsibleParty
      | undefined
  }

  set responsibleParty(value: ResponsibleParty | undefined) {
    if (value) {
      this._responsibleParty = codeableConcept({
        system: 'https://www.seenhealth.org/fhir/CodeSystem/responsible-party',
        code: value,
        display: value,
      })
    } else {
      this._responsibleParty = undefined
    }
  }

  static searchQuery(): QueryTypes {
    return {
      ...Participant.baseSearchQuery(),
      _tag: LEAD_TAG,
    }
  }

  static buildFromNewLeadQuestionnaire(data: {
    identifier?: Identifier
    firstName: string
    lastName: string
    preferredName?: string
    contactMethod: LeadContactMethod[]
    address: Address
    dateOfBirth?: string
    genderId?: CodeableConcept
    languageCodes?: ProfileLanguageCode[]
  }): Lead {
    const gender = codeFromCodeableConcept(
      data.genderId
    ) as Participant['gender']

    const telecom: ContactPoint[] = data.contactMethod.map((contactMethod) => ({
      system: codeFromCodeableConcept(contactMethod.contactMethodType) as
        | 'phone'
        | 'email',
      value: contactMethod.contactMethodValue,
      rank:
        data.contactMethod.length === 1 || contactMethod.contactMethodPreferred
          ? 1
          : 2,
      use: 'home',
    }))

    const newLead = new Lead({
      identifier: data.identifier ? [data.identifier] : undefined,
      name: compact([
        {
          family: data.lastName,
          given: [data.firstName],
          use: 'official' as const,
        },
        data.preferredName !== undefined
          ? {
              given: [data.preferredName],
              use: 'nickname' as const,
            }
          : undefined,
      ]),
      birthDate:
        data.dateOfBirth ??
        // setting a sentinel value
        '1900-01-01',
      gender,
      telecom: telecom.length > 0 ? telecom : undefined,
      address: [data.address],
    })
    newLead.languageCodes = data.languageCodes ?? []
    return newLead
  }

  protected getExportFields(): ExportFields {
    return {
      leadStatus: this.leadStatus,
      responsibleParty: this.responsibleParty,
      medicaidEligible: this.medicaidEligibleBoolean,
      medicareEligible: this.medicareEligibleBoolean,
      medicareMedicalStatus: this.medicareMedicalStatus,
      otherNotes: this.otherNotes,
      flags: this.flags,
    }
  }
}
